import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import './TermsOfService.css';
import logoWhite from '../../images/logo_white.png';

const TermsOfService = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const currentYear = new Date().getFullYear();

  const sections = [
    { id: 'acceptance', label: 'Acceptance of Terms' },
    { id: 'description', label: 'Description of Service' },
    { id: 'accounts', label: 'User Accounts' },
    { id: 'subscriptions', label: 'Subscriptions and Payments' },
    { id: 'acceptable-use', label: 'Acceptable Use' },
    { id: 'intellectual-property', label: 'Intellectual Property' },
    { id: 'data-privacy', label: 'Data and Privacy' },
    { id: 'confidentiality', label: 'Confidentiality' },
    { id: 'termination', label: 'Termination' },
    { id: 'disclaimers', label: 'Disclaimers' },
    { id: 'liability', label: 'Limitation of Liability' },
    { id: 'governing-law', label: 'Governing Law' },
    { id: 'changes', label: 'Changes to Terms' },
    { id: 'contact', label: 'Contact Us' },
  ];

  return (
    <div className="tos-container">

      {/* Navbar */}
      <nav className="tos-navbar">
        <div className="tos-nav-content">
          <div className="tos-logo" onClick={() => window.location.href = '/'}>
            <img src={logoWhite} alt="TalentScope360 Logo" />
          </div>

          <button
            className="tos-mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="tos-nav-links tos-nav-desktop">
            <a href="/#features" className="tos-nav-link">Features</a>
            <a href="/pricing" className="tos-nav-link">Pricing</a>
            <a href="/documentation" className="tos-nav-link">Documentation</a>
          </div>

          <div className="tos-nav-buttons tos-nav-desktop">
            <a href="/login" className="tos-btn-login">Login</a>
            <a href="/sign-up" className="tos-btn-getstarted">Get Started</a>
          </div>
        </div>

        <div className={`tos-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <a href="/#features" className="tos-mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>Features</a>
          <a href="/#faq" className="tos-mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
          <a href="/documentation" className="tos-mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>Documentation</a>
          <div className="tos-mobile-menu-buttons">
            <a href="/login" className="tos-btn-login">Login</a>
            <a href="/sign-up" className="tos-btn-getstarted">Get Started</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="tos-hero">
        <div className="tos-hero-pattern" aria-hidden="true" />
        <div className="tos-hero-content">
          <h1 className="tos-hero-title">Terms of Service</h1>
          <div className="tos-hero-accent" aria-hidden="true" />
          <p className="tos-hero-subtitle">
            Please read these terms carefully before using TalentScope360.
          </p>
          <div className="tos-hero-meta">
            <span className="tos-hero-chip">
              <span className="tos-hero-chip-dot" />
              Effective: 1st January 2024
            </span>
            <span className="tos-hero-chip-divider" />
            <span className="tos-hero-chip">
              <span className="tos-hero-chip-dot tos-hero-chip-dot--updated" />
              Last Updated: 1st January 2026
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="tos-body">

        {/* Sidebar TOC */}
        <aside className="tos-sidebar">
          <p className="tos-sidebar-title">Contents</p>
          <nav className="tos-toc">
            {sections.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="tos-toc-link">{s.label}</a>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="tos-main">

          <div className="tos-intro-card">
            <p>
              These Terms of Service ("Terms") govern your access to and use of the TalentScope360 platform
              ("Service"), operated by <strong>ARTEMIS Transition Partners</strong> ("Company", "we", "us", or "our").
              By accessing or using the Service, you agree to be bound by these Terms. If you do not agree,
              please discontinue use immediately.
            </p>
          </div>

          {/* 1 */}
          <section id="acceptance" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">01</span>
              Acceptance of Terms
            </h2>
            <p>
              By registering for, accessing, or using TalentScope360, you confirm that you are at least 18 years
              old, have the legal authority to enter into this agreement on behalf of yourself or your organization,
              and agree to comply with these Terms and all applicable laws and regulations.
            </p>
            <p>
              If you are accepting these Terms on behalf of an organization, you represent and warrant that you have
              the authority to bind that organization to these Terms, and all references to "you" in these Terms
              refer to that organization.
            </p>
          </section>

          {/* 2 */}
          <section id="description" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">02</span>
              Description of Service
            </h2>
            <p>
              TalentScope360 is a comprehensive, competency-based talent management platform that enables
              organizations to plan, assess, develop, and manage workforce capabilities. The Service includes:
            </p>
            <ul className="tos-list">
              <li>Talent strategy and competency framework design</li>
              <li>Organizational structure visualization and management</li>
              <li>Role-level competency modelling and talent planning</li>
              <li>Individual and team competency evaluations and assessments</li>
              <li>Talent review, performance reporting, and analytics dashboards</li>
              <li>Succession planning and talent pipeline management</li>
              <li>Learning and development alignment tools</li>
            </ul>
            <p>
              The Service is provided on a subscription basis. Features available to you depend on your
              subscription plan and the permissions configured by your organization's administrator.
            </p>
            <div className="tos-highlight-box">
              <strong>Important:</strong> TalentScope360 measures and evaluates competence — the underlying
              drivers of performance — and does <em>not</em> track KPIs, performance targets, or payroll data.
            </div>
          </section>

          {/* 3 */}
          <section id="accounts" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">03</span>
              User Accounts
            </h2>
            <p>
              To access the Service, you must create an account and provide accurate, complete, and current
              information. You are responsible for:
            </p>
            <ul className="tos-list">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activity that occurs under your account</li>
              <li>Notifying us immediately at <a href="mailto:support@talentscope360.com" className="tos-link">support@talentscope360.com</a> of any unauthorized access or security breach</li>
              <li>Ensuring that all users added to your organization's account comply with these Terms</li>
            </ul>
            <p>
              Organization administrators are responsible for managing user access, roles, and permissions
              within their account. We reserve the right to suspend or terminate accounts that violate these Terms.
            </p>
          </section>

          {/* 4 */}
          <section id="subscriptions" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">04</span>
              Subscriptions and Payments
            </h2>
            <p>
              Access to certain features of TalentScope360 requires a paid subscription. By subscribing, you agree to
              the following:
            </p>
            <ul className="tos-list">
              <li><strong>Billing:</strong> Subscription fees are billed in advance on a monthly or annual cycle as selected at sign-up.</li>
              <li><strong>Renewal:</strong> Subscriptions automatically renew unless cancelled before the renewal date.</li>
              <li><strong>Cancellation:</strong> You may cancel your subscription at any time through your account settings. Access continues until the end of the current billing period.</li>
              <li><strong>Refunds:</strong> Fees are non-refundable except where required by applicable law or as expressly stated in your subscription agreement.</li>
              <li><strong>Price Changes:</strong> We may modify subscription pricing with at least 30 days' written notice. Continued use after the notice period constitutes acceptance.</li>
              <li><strong>Taxes:</strong> Prices exclude applicable taxes, which will be added at checkout where required by law.</li>
            </ul>
          </section>

          {/* 5 */}
          <section id="acceptable-use" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">05</span>
              Acceptable Use
            </h2>
            <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You must not:</p>
            <ul className="tos-list">
              <li>Use the Service to process, store, or transmit data in violation of any applicable law or regulation</li>
              <li>Attempt to gain unauthorized access to any part of the Service or its underlying infrastructure</li>
              <li>Reverse engineer, decompile, or disassemble any part of the platform</li>
              <li>Use the Service to harass, discriminate against, or harm any individual</li>
              <li>Upload malicious code, viruses, or any software designed to disrupt or damage the Service</li>
              <li>Scrape, crawl, or extract data from the Service using automated means without prior written consent</li>
              <li>Resell, sublicense, or commercially exploit the Service without authorization</li>
              <li>Use assessment or competency data to make employment decisions in a manner that violates labour or employment laws</li>
            </ul>
            <p>
              We reserve the right to investigate violations and take appropriate action, including suspending or
              terminating access and reporting conduct to relevant authorities.
            </p>
          </section>

          {/* 6 */}
          <section id="intellectual-property" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">06</span>
              Intellectual Property
            </h2>
            <p>
              All content, features, functionality, methodologies, competency frameworks, and technology comprising
              TalentScope360 — including but not limited to software code, design, trademarks, logos, and
              documentation — are the exclusive property of ARTEMIS Transition Partners and are protected by
              applicable intellectual property laws.
            </p>
            <p>
              We grant you a limited, non-exclusive, non-transferable, revocable licence to access and use the
              Service solely for your internal organizational purposes in accordance with these Terms. This licence
              does not include the right to:
            </p>
            <ul className="tos-list">
              <li>Copy, modify, or create derivative works of the Service or its content</li>
              <li>Use our trademarks, logos, or branding without prior written consent</li>
              <li>Export or reproduce any competency frameworks or methodologies for commercial distribution</li>
            </ul>
            <p>
              <strong>Your Data:</strong> You retain full ownership of all data, content, and information you upload
              or input into the Service ("Customer Data"). By using the Service, you grant us a limited licence to
              process your Customer Data solely to provide and improve the Service.
            </p>
          </section>

          {/* 7 */}
          <section id="data-privacy" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">07</span>
              Data and Privacy
            </h2>
            <p>
              Your use of the Service is also governed by our{' '}
              <a href="/privacy-policy" className="tos-link">Privacy Policy</a>, which is incorporated into these
              Terms by reference. We are committed to protecting the security and integrity of your data.
            </p>
            <p>Key data commitments include:</p>
            <ul className="tos-list">
              <li>Your data is stored securely using industry-standard infrastructure (Supabase)</li>
              <li>We do not sell your data to third parties</li>
              <li>Assessment and competency data is processed only for the purposes you direct</li>
              <li>You may request export or deletion of your data at any time</li>
            </ul>
            <p>
              For organizations operating in regulated jurisdictions, it is your responsibility to ensure that
              your use of TalentScope360 complies with applicable data protection legislation (e.g., GDPR, POPIA, CCPA).
            </p>
          </section>

          {/* 8 */}
          <section id="confidentiality" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">08</span>
              Confidentiality
            </h2>
            <p>
              Both parties agree to treat as confidential all non-public information disclosed in connection with
              the Service, including competency data, assessment results, organizational structures, and business
              strategies. This obligation survives termination of these Terms.
            </p>
            <p>
              You agree not to disclose individual assessment scores or competency evaluations to unauthorized
              personnel within or outside your organization, except as required by law or with the explicit consent
              of the assessed individual.
            </p>
          </section>

          {/* 9 */}
          <section id="termination" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">09</span>
              Termination
            </h2>
            <p>
              Either party may terminate these Terms at any time. We may suspend or terminate your access
              immediately, without prior notice or liability, if:
            </p>
            <ul className="tos-list">
              <li>You breach any provision of these Terms</li>
              <li>You fail to pay applicable fees</li>
              <li>We are required to do so by law or regulatory authority</li>
              <li>Continuing to provide the Service poses legal, security, or reputational risk</li>
            </ul>
            <p>
              Upon termination, your right to use the Service ceases immediately. You may request a data export
              within 30 days of termination, after which your data may be permanently deleted. Provisions that by
              their nature should survive termination (including intellectual property, confidentiality, and
              limitation of liability) shall do so.
            </p>
          </section>

          {/* 10 */}
          <section id="disclaimers" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">10</span>
              Disclaimers
            </h2>
            <p>
              The Service is provided on an "as is" and "as available" basis without warranty of any kind. To the
              fullest extent permitted by law, ARTEMIS Transition Partners expressly disclaims all warranties,
              whether express, implied, or statutory, including:
            </p>
            <ul className="tos-list">
              <li>Implied warranties of merchantability and fitness for a particular purpose</li>
              <li>Warranties that the Service will be uninterrupted, error-free, or secure</li>
              <li>Warranties regarding the accuracy or completeness of any competency assessments or reports</li>
            </ul>
            <p>
              Competency assessments and talent reviews produced by TalentScope360 are tools to inform
              decision-making and should not be the sole basis for employment decisions, promotions, or disciplinary actions.
            </p>
          </section>

          {/* 11 */}
          <section id="liability" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">11</span>
              Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by applicable law, ARTEMIS Transition Partners shall not be liable
              for any indirect, incidental, special, consequential, or punitive damages arising from your use of
              or inability to use the Service, including but not limited to:
            </p>
            <ul className="tos-list">
              <li>Loss of profits, revenue, or business opportunities</li>
              <li>Loss or corruption of data</li>
              <li>Costs of substitute services</li>
              <li>Employment-related claims arising from decisions made using assessment data</li>
            </ul>
            <p>
              Our total aggregate liability to you for any claim arising out of or related to these Terms or the
              Service shall not exceed the total fees paid by you in the 12 months preceding the claim.
            </p>
          </section>

          {/* 12 */}
          <section id="governing-law" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">12</span>
              Governing Law
            </h2>
            <p>
              These Terms shall be governed by and construed in accordance with applicable law. Any disputes
              arising under or in connection with these Terms shall first be subject to good-faith negotiation
              between the parties. If unresolved, disputes shall be submitted to binding arbitration or the
              jurisdiction of the competent courts in the territory where ARTEMIS Transition Partners is registered.
            </p>
          </section>

          {/* 13 */}
          <section id="changes" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">13</span>
              Changes to Terms
            </h2>
            <p>
              We reserve the right to update or modify these Terms at any time. We will notify you of material
              changes by posting the updated Terms on this page and updating the "Last Updated" date, and where
              appropriate, by sending an email to your registered address.
            </p>
            <p>
              Your continued use of the Service after the effective date of the revised Terms constitutes
              your acceptance of the changes. If you do not agree to the updated Terms, you must discontinue
              use of the Service.
            </p>
          </section>

          {/* 14 */}
          <section id="contact" className="tos-section">
            <h2 className="tos-section-heading">
              <span className="tos-section-number">14</span>
              Contact Us
            </h2>
            <p>If you have any questions or concerns about these Terms, please contact us:</p>
            <div className="tos-contact-card">
              <p><strong>ARTEMIS Transition Partners</strong></p>
              <p>Operating: TalentScope360</p>
              <p>Email: <a href="mailto:support@talentscope360.com" className="tos-link">support@talentscope360.com</a></p>
              <p>Website: <a href="https://www.talentscope360.com" className="tos-link" target="_blank" rel="noreferrer">www.talentscope360.com</a></p>
            </div>
          </section>

          <div className="tos-footer-note">
            <p>
              By using TalentScope360, you acknowledge that you have read, understood, and agree to be bound
              by these Terms of Service.
            </p>
            <div className="tos-legal-links">
              <a href="/privacy-policy" className="tos-link">Privacy Policy</a>
              <span className="tos-divider">|</span>
              <a href="/" className="tos-link">Back to Home</a>
            </div>
          </div>

        </main>
      </div>

      {/* Footer */}
      <footer className="tos-footer-bottom">
        <p>© {currentYear} TalentScope360 | All rights reserved. | <a href="/terms-of-service" className="tos-footer-link">Terms of Service</a> &nbsp;|&nbsp; <a href="/privacy-policy" className="tos-footer-link">Privacy Policy</a></p>
      </footer>

    </div>
  );
};

export default TermsOfService;
